import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Box, FormControl, InputLabel, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';

interface PhoneInputWithLabelProps {
  value: string;
  onChange: (value: string, country?: any, e?: React.ChangeEvent<HTMLInputElement>, formattedValue?: string) => void;
  label: string;
  helperText?: string;
  countryCodeEditable?: boolean;
  disableDropdown?: boolean;
  disableCountryCode?: boolean;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  name?: string;
}

const StyledPhoneInputContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  '& .react-tel-input': {
    '& .form-control': {
      width: '100%',
      height: '56px',
      borderRadius: '4px',
      fontSize: '16px',
      paddingLeft: '52px',
      border: '1px solid rgba(0, 0, 0, 0.23)',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:focus': {
        border: `1px solid ${theme.palette.primary[400]}`,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}20`,
      },
      '&:hover:not(:focus)': {
        borderColor: 'rgba(0, 0, 0, 0.87)',
      },
    },
    '& .flag-dropdown': {
      background: 'transparent',
      border: 'none',
      borderRadius: '4px 0 0 4px',
      '&.open': {
        background: 'transparent',
      },
    },
    '& .selected-flag': {
      backgroundColor: 'transparent',
      width: '52px',
      '&:hover, &:focus': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
  },
}));

const StyledPhoneInputContainerWithError = styled(StyledPhoneInputContainer, {
  shouldForwardProp: (prop) => prop !== 'error',
})<{ error?: boolean }>(({ theme, error }) => ({
  '& .react-tel-input .form-control': {
    borderColor: error ? theme.palette.error.main : 'rgba(0, 0, 0, 0.23)',
    '&:focus': {
      borderColor: error ? theme.palette.error.main : theme.palette.primary[400],
      boxShadow: error ? `0 0 0 1px ${theme.palette.error.main}20` : `0 0 0 1px ${theme.palette.primary[400]}20`,
    },
    '&:hover:not(:focus)': {
      borderColor: error ? theme.palette.error.main : 'rgba(0, 0, 0, 0.87)',
    },
  },
}));

const FloatingLabel = styled(InputLabel)(({ }) => ({
  position: 'absolute',
  left: '52px',
  top: 0,
  fontSize: '12px',
  transform: 'translate(0, -50%) scale(0.75)',
  transformOrigin: 'left top',
  backgroundColor: 'white',
  padding: '0 4px',
  zIndex: 1,
}));

interface FloatingLabelErrorProps {
  error?: boolean;
}

const FloatingLabelWithError = styled(FloatingLabel, {
  shouldForwardProp: (prop) => prop !== 'error',
})<FloatingLabelErrorProps>(({ theme, error }) => ({
  color: error ? theme.palette.error.main : theme.palette.primary[400],
}));

const PhoneInputWithLabel: React.FC<PhoneInputWithLabelProps> = ({
  value,
  onChange,
  label,
  helperText,
  disableDropdown = true,
  disableCountryCode = true,
  disabled = false,
  error = false,
  required = false,
  name = 'phoneNumber',
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = Boolean(value);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const handleChange = (val: string, country: any, e: React.ChangeEvent<HTMLInputElement>, formattedValue: string) => {
    onChange(val, country, e, formattedValue);
  };

  return (
    <FormControl fullWidth error={error} required={required}>
      <StyledPhoneInputContainerWithError error={error}>
        {(isFocused || hasValue) && (
          <FloatingLabelWithError shrink={true} error={error}>
            {label}
          </FloatingLabelWithError>
        )}
        <PhoneInput
          country={'us'}
          value={value}
          onChange={handleChange}
          inputProps={{
            name: name,
            placeholder: !isFocused && !hasValue ? label : '',
            onFocus: handleFocus,
            onBlur: handleBlur,
            disabled: disabled,
            required: required,
            'aria-invalid': error,
            'aria-describedby': helperText ? `${name}-helper-text` : undefined,
          }}
          disableDropdown={disableDropdown}
          onlyCountries={['us']}
          disableCountryCode={disableCountryCode}
          disabled={disabled}
          inputStyle={{
            background: disabled ? "#f0f0f0" : 'unset'
          }}
          containerClass={error ? 'error' : ''}
        />
      </StyledPhoneInputContainerWithError>
      {helperText && (
        <FormHelperText
          id={`${name}-helper-text`}
          error={error}
          sx={{ margin: '4px 14px 0', lineHeight: 1.5 }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default PhoneInputWithLabel;